import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Toon } from "@/components/Toon";
import { partnerHref } from "@/lib/links";

const EASE = [0.76, 0, 0.24, 1];

const SERVICES = [
  {
    id: "insurance",
    n: "01",
    icon: "🛡️",
    title: "Employee Health Insurance Savings",
    body: "Strategic options that reduce employer group healthcare costs — without sacrificing employee benefit coverage.",
    tags: ["Cost Reduction", "Benefits Strategy"],
    cta: {
      label: "Reduce Employer Costs",
      href: "https://ehpinc.com/?aff=102645",
    },
  },
  {
    id: "energy",
    n: "02",
    icon: "⚡",
    title: "Energy Cost Reductions",
    body: "Electric supply and storage, natural gas, EV charging, and solar solutions built for business.",
    tags: ["Electric", "Natural Gas", "EV Charging", "Solar", "Storage"],
    cta: {
      label: "Get an Energy Quote",
      href: "https://calendly.com/callmegerard/lets-talk",
    },
  },
  {
    id: "finance",
    n: "03",
    icon: "📊",
    title: "Business & Real Estate Finance",
    body: "Flexible funding options for businesses and real estate investors — capital that fits your next move, structured around your goals.",
    tags: ["Commercial Funding", "Real Estate Investment"],
    cta: {
      label: "Get Business Funding",
      href: "https://go.mypartner.io/business-financing/?ref=001Qk00000o1V9sIAE",
    },
    cta2: {
      label: "Start-up Funding",
      href: "https://lexingtoncapitalholdings.formtitan.com/ftproject/ft58c846d515a448fb990fb4e95ba6b5ce?ref=001Uo00000DbEJ3",
    },
    cta3: {
      label: "Commercial Real-Estate Funding",
      href: "https://calendly.com/callmegerard/lets-talk",
    },
  },
  {
    id: "processing",
    n: "04",
    icon: "💳",
    title: "Payment Card Processing",
    body: "Best-in-class card payment processing for operations of every size — faster settlements, lower fees, and hardware that just works.",
    tags: ["Lower Fees", "Fast Settlements", "POS & Online"],
    cta: {
      label: "Get a Processing Quote",
      href: "https://calendly.com/callmegerard/lets-talk",
    },
  },
  {
    id: "speaking",
    n: "05",
    icon: "🧑🏽‍🏫",
    title: "Public Speaking & Contract Negotiation",
    body: "Custom presentations that sharpen your team's edge — and expert, on-demand negotiations for your most important deals.",
    tags: ["Keynotes", "Team Training", "Deal Negotiation"],
    wide: true,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="mx-auto max-w-[90rem] px-6 py-28 md:px-12 md:py-40"
    >
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-6 text-xs uppercase tracking-[0.35em] text-steel"
      >
        Services
      </motion.p>
      <div className="mb-20 flex items-end justify-between gap-8">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="max-w-3xl font-serif text-4xl leading-tight tracking-tight text-fog md:text-6xl"
        >
          Five ways I{" "}
          <span className="italic text-steel">maximize your business momentum</span>
        </motion.h2>
        <Toon
          testId="services-toon"
          variant="point"
          imgClass="w-40 md:w-48"
          className="hidden shrink-0 sm:block"
        />
      </div>

      <div className="grid grid-cols-1 gap-px bg-white/10 lg:grid-cols-2">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`service-card-${s.id}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: (i % 2) * 0.1 }}
            className={`group relative bg-ink p-10 transition-colors duration-500 hover:bg-panel md:p-14 ${
              s.wide ? "lg:col-span-2" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-[0.3em] text-dim">{s.n}</span>
              <span className="text-3xl">{s.icon}</span>
            </div>
            <div className="mt-8 flex items-start justify-between gap-6">
              <h3 className="max-w-xl font-serif text-3xl tracking-tight text-fog md:text-4xl">
                {s.title}
              </h3>
              <ArrowUpRight className="h-6 w-6 shrink-0 text-dim transition-[transform,color] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-steel" />
            </div>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ash md:text-lg">
              {s.body}
            </p>
            <div className="mt-10 flex flex-wrap gap-2">
              {s.tags.map((t) => (
                <span
                  key={t}
                  className="border border-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors duration-300 group-hover:border-steel/40"
                >
                  {t}
                </span>
              ))}
            </div>
            {(s.cta || s.cta2 || s.cta3) && (
              <div className="mt-10 flex flex-wrap gap-3">
                {s.cta && (
                  <a
                    data-testid={`service-cta-${s.id}`}
                    href={partnerHref(s.cta.href, `${s.title} — ${s.cta.label}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
                  >
                    {s.cta.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
                {s.cta2 && (
                  <a
                    data-testid={`service-cta2-${s.id}`}
                    href={partnerHref(s.cta2.href, `${s.title} — ${s.cta2.label}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-fog transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-steel hover:bg-steel/10"
                  >
                    {s.cta2.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
                {s.cta3 && (
                  <a
                    data-testid={`service-cta3-${s.id}`}
                    href={partnerHref(s.cta3.href, `${s.title} — ${s.cta3.label}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-fog transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-steel hover:bg-steel/10"
                  >
                    {s.cta3.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
